import { generateId } from '@ai-sdk/provider-utils';
import type Stripe from 'stripe';
import { chatHooks } from '~/lib/chat-hooks';
import { stripeStore } from '~/lib/stores/stripe';
import { stripIndents } from '~/utils/stripIndent';
import { formatDefaultPriceForProduct } from '~/utils/stripe-helpers';

export function sendMessageWithProductSelection(selectedProducts: Set<string>) {
  /**
   * We use the product selection to inject the message we send
   * to bolt to trigger the code path where it auto gen the
   * files we definitely want to see and it modify the rest
   * of the application.
   */
  const stripeProducts = stripeStore.products
    .get()
    .filter(
      (product) =>
        selectedProducts.has(product.id) && product.default_price && typeof product.default_price !== 'string',
    ) as (Stripe.Product & { default_price: Stripe.Price })[];

  if (stripeProducts.length === 0) {
    throw new Error('No valid product where selected!');
  }

  const content = [
    'I want my application to accept payments for the following products:',
    '',
    '<div class="__boltCollapsible__" data-title="Stripe Products">',
    '',
    stripeProducts
      .map(
        (product, index) => stripIndents`
          ${index + 1}. **${product.name}**:
            * Description: ${product.description}
            * Price: ${formatDefaultPriceForProduct(product)}
        `,
      )
      .join('\n'),
    '',
    '</div>',
  ].join('\n');

  /**
   * Ideally bolt tells us which product are now part of the selection.
   *
   * This optimistic update might not be correct if bolt has ignored
   * some of the product and not integrated them.
   *
   * However we already assume that bolt will update them in /api/chat.
   */
  stripeStore.selectedProducts.set([...selectedProducts]);

  chatHooks.setMessages((messages) => {
    const newMessages = [...messages];

    newMessages.push({
      role: 'user',
      type: 'chat',
      id: generateId(),
      renderHtml: true,
      stripeIntegration: {
        // if we dont have edge functions, we need to go through the initial setup
        phase: stripeStore.hasEdgeFunctions.get() ? 'update-products' : 'initial-setup',
        mode: stripeStore.mode.get(),
        products: stripeProducts.map((product) => ({
          id: product.id,
          name: product.name,
          description: product.description,
          default_price: {
            id: product.default_price.id,
            type: product.default_price.type,
          },
        })),
      },
      content,
      status: 'complete',
      parts: [],
    });

    return newMessages;
  });

  chatHooks.reload();
}
